import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { SEO } from "./components/SEO";
import { AdminPage } from "./pages/AdminPage";
import { PrivacyPolicy } from "./pages/PrivacyPolicy";
import { ThankYou } from "./pages/ThankYou";
import { FranchiseOpportunity } from './pages/FranchiseOpportunity';
import { SponsorshipOpportunity } from './pages/SponsorshipOpportunity';
import { PressMedia } from './pages/PressMedia';
import { Resources } from './pages/Resources';
import { TrainingAcademy } from './pages/TrainingAcademy';
import ApplyPage from './pages/ApplyPage';


// Load HumanChat bot script
const loadHumanChatBot = () => {
  // Check if already loaded
  if (document.getElementById('humanchat-script')) {
    return;
  }

  // Load the HumanChat library
  const script = document.createElement('script');
  script.id = 'humanchat-script';
  script.type = 'text/javascript';
  script.src = 'https://humanchat.net/build/js/hb_latest.js?v=1.5.28';
  script.crossOrigin = 'anonymous';
  script.setAttribute('data-cfasync', 'false');
  
  script.onload = () => {
    // Initialize the bot after script loads
    if (window.AiBot) {
      try {
        window.aiBot = new window.AiBot({
          embedId: 'k5d2Ns5uSaaH',
          remoteBaseUrl: 'https://humanchat.net/',
          version: '1.5.28'
        });
      } catch (error) {
        // Silent fail for chat widget
      }
    }
  };
  
  script.onerror = () => {
    // Silent fail for external script
  };
  
  document.body.appendChild(script);
};
import { Header } from "./components/ui/header";
import { HeroSection } from "./components/ui/hero-section";
import { WhyChooseSection } from "./components/ui/why-choose-section";
import { AboutFranchiseSection } from "./components/ui/about-franchise";
import { TestimonialsSection } from "./components/ui/testimonials-section";
import { HowItWorksSection } from "./components/ui/how-it-works";
import { InvestmentSection } from "./components/ui/investment-section";
import { LeadFormSection } from "./components/ui/lead-form";
import { ClosingSection } from "./components/ui/closing-section";
import { Footer } from "./components/ui/footer";
import { FounderSection } from "./components/ui/founder-section";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import axios from "axios";
import { CookieConsent } from "./components/ui/cookie-consent";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Main Landing Page Component
const LandingPage = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize chatbot after component mounts
  useEffect(() => {
    // Add global error handler to prevent error overlay for external scripts
    const handleGlobalError = (event) => {
      // Suppress errors from external scripts
      if (event.filename && event.filename.includes('gadget.js')) {
        event.preventDefault();
        return true;
      }
    };

    window.addEventListener('error', handleGlobalError);
    
    // Backup chat widget function
    const showBackupChatWidget = () => {
      setTimeout(() => {
        const chatWidget = document.getElementById('backup-chat-widget');
        if (chatWidget) {
          chatWidget.style.display = 'block';
        }
      }, 3000);
    };

    // Initialize after a short delay to ensure DOM is ready
    const timer = setTimeout(() => {
      showBackupChatWidget();
    }, 2000);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('error', handleGlobalError);
    };
  }, []);

  // Handle form submission with backend API
  const handleFormSubmission = async (formData) => {
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/applications`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        requestType: formData.requestType || 'application'
      });
      
      if (response.data.success) {
        if (response.data.brochure_url) {
          // For brochure requests - redirect to thank you page with brochure
          navigate(`/thank-you?type=brochure&brochure=${encodeURIComponent(response.data.brochure_url)}`);
        } else {
          // For regular applications - redirect to thank you page
          navigate('/thank-you?type=application');
        }
      }
      
    } catch (error) {
      if (error.response?.data?.detail) {
        // Handle specific backend errors
        if (error.response.data.detail.includes("email already exists")) {
          toast.error("Already registered", {
            description: "We already have your details. We'll be in touch soon about your franchise interest!"
          });
        } else if (error.response.data.detail.includes("Validation error")) {
          toast.error("Invalid information", {
            description: "Please check your information and try again."
          });
        } else {
          toast.error("Submission failed", {
            description: error.response.data.detail
          });
        }
      } else if (error.code === 'ERR_NETWORK') {
        // BACKUP SYSTEM: Network error - direct users to chat
        toast.error("Connection issue detected", {
          description: "Don't worry! Click the 'Have a Chat' button (bottom-left) to reach us directly!",
          duration: 8000
        });
      } else {
        // BACKUP SYSTEM: Any other error - direct users to chat
        toast.error("Form temporarily unavailable", {
          description: "No problem! Click 'Have a Chat' (bottom-left corner) or call us for immediate assistance!",
          duration: 8000
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Smooth scroll to form section
  const scrollToForm = () => {
    const formElement = document.getElementById('apply-form');
    if (formElement) {
      formElement.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  return (
    <div>
      <SEO page="home" />
      <Header onApplyClick={scrollToForm} />
      
      <HeroSection 
        onApplyClick={scrollToForm}
        onScrollToForm={scrollToForm}
      />
      
      <WhyChooseSection />
      
      {/* Early Lead Capture Form - After Franchise Intro */}
      <LeadFormSection 
        onSubmit={handleFormSubmission}
        isSubmitting={isSubmitting}
        variant="compact"
        sectionId="early-apply-form"
      />
      
      {/* Free Report Banner - Links to Training Academy */}
      <a 
        href="/training-academy" 
        className="block w-full cursor-pointer hover:opacity-95 transition-opacity"
        data-testid="free-report-banner"
      >
        <img 
          src="https://customer-assets-lqy194kg.emergentagent.net/job_f194cab7-0920-47a1-bac9-625d43d4e059/artifacts/brum24d6_Virtual%20Speedy%20Services%20free%20report_20260817_180120_0000.webp"
          alt="STOP! Read This Before You Spend Thousands Starting a Hair Business - Download Free Report by Diane Shawe MEd"
          className="w-full h-auto"
        />
      </a>
      
      <FounderSection />
      
      <AboutFranchiseSection />
      
      {/* Quiz Assessment Banner */}
      <a 
        href="https://www.qualiquiz.app/quiz/salon-franchise-candidate-pre-qualification-asses"
        target="_blank"
        rel="noopener noreferrer"
        className="block w-full cursor-pointer hover:opacity-95 transition-opacity"
        data-testid="quiz-banner-home"
      >
        <img 
          src="https://customer-assets-lqy194kg.emergentagent.net/job_f194cab7-0920-47a1-bac9-625d43d4e059/artifacts/ijhd71jb_Franchise%20Candidatepre%20assessment%20needahairmakeover%202.jpg"
          alt="Salon Franchise Candidate Pre-Qualification Assessment - Are you ready to own a specialist salon? Take the Assessment"
          className="w-full h-auto"
        />
      </a>
      
      <TestimonialsSection />
      
      <HowItWorksSection />
      
      <InvestmentSection onApplyClick={scrollToForm} />
      
      <LeadFormSection 
        onSubmit={handleFormSubmission}
        isSubmitting={isSubmitting}
      />
      
      <ClosingSection onApplyClick={scrollToForm} />
      
      <Footer />
      
      {/* Backup Chat Widget - Bottom Left */}
      <div 
        id="backup-chat-widget" 
        className="fixed bottom-6 left-6 z-50"
        style={{ display: 'none' }}
      >
        <div className="bg-accent-primary text-white p-4 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 cursor-pointer group animate-pulse hover:animate-none">
          <a 
            href="https://brochure.needahairmakeoverfranchise.co.uk" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center space-x-3"
          >
            <div className="w-6 h-6 flex items-center justify-center">
              💬
            </div>
            <span className="hidden group-hover:block text-sm font-medium whitespace-nowrap">
              Chat with us!
            </span>
          </a>
        </div>
      </div>
      
      {/* Always Visible Chat Widget - Bottom Left */}  
      <div className="fixed bottom-6 left-6 z-50">
        <div className="bg-white border-2 border-accent-primary text-accent-primary p-4 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 cursor-pointer group hover:bg-accent-primary hover:text-white">
          <a 
            href="https://brochure.needahairmakeoverfranchise.co.uk" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center space-x-3"
            title="Chat with our franchise experts"
          >
            <div className="w-6 h-6 flex items-center justify-center text-lg">
              💬
            </div>
            <span className="text-sm font-medium whitespace-nowrap pr-2">
              Have a Chat
            </span>
          </a>
        </div>
      </div>
    </div>
  );
};

function App() {
  // Load HumanChat bot when app mounts
  useEffect(() => {
    loadHumanChatBot();
  }, []);

  return (
    <HelmetProvider>
      <BrowserRouter>
        <div className="App">
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/apply" element={<ApplyPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/thank-you" element={<ThankYou />} />
            <Route path="/hair-extensions-franchise-opportunity" element={<FranchiseOpportunity />} />
            <Route path="/sponsorship-opportunity" element={<SponsorshipOpportunity />} />
            <Route path="/press" element={<PressMedia />} />
            <Route path="/resources" element={<Resources />} />
            <Route path="/training-academy" element={<TrainingAcademy />} />
          </Routes>
          
          {/* Toast notifications */}
          <Toaster position="top-right" />
          
          {/* Cookie Consent Banner */}
          <CookieConsent />
        </div>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;